import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { InstallPrompt } from "@/components/InstallPrompt";
import Layout from "@/components/Layout";
import Home from "@/pages/Home";
import Login from "@/pages/Login";
import PartnerSignup from "@/pages/PartnerSignup";
import PartnerPortal from "@/pages/PartnerPortal";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import Leads from "@/pages/Leads";
import Pipeline from "@/pages/Pipeline";
import Partners from "@/pages/Partners";
import Lenders from "@/pages/Lenders";
import Commissions from "@/pages/Commissions";
import Reports from "@/pages/Reports";
import Notifications from "@/pages/Notifications";
import Users from "@/pages/Users";
import Products from "@/pages/Products";
import Roles from "@/pages/Roles";
import PartnerTypes from "@/pages/PartnerTypes";
import EmailSettings from "@/pages/EmailSettings";
import DataBackup from "@/pages/DataBackup";
import EmailNotifications from "@/pages/EmailNotifications";
import Marketing from "@/pages/Marketing";
import MetaSettings from "@/pages/MetaSettings";
import Audit from "@/pages/Audit";
import Finance from "@/pages/Finance";
import BillItems from "@/pages/BillItems";
import Website from "@/pages/Website";
import MasterDocuments from "@/pages/MasterDocuments";
import ProposalFormat from "@/pages/ProposalFormat";
import BillFormat from "@/pages/BillFormat";
import RoleHierarchy from "@/pages/RoleHierarchy";
import FieldRules from "@/pages/FieldRules";
import AgreementFormat from "@/pages/AgreementFormat";
import Leegality from "@/pages/Leegality";
import Pdd from "@/pages/Pdd";
import OpsApproval from "@/pages/OpsApproval";
import CompanyBanks from "@/pages/CompanyBanks";
import UndertakingFormat from "@/pages/UndertakingFormat";
import OpsCheck from "@/pages/OpsCheck";
import Offers from "@/pages/Offers";
import OffersView from "@/pages/OffersView";
import FinalPdd from "@/pages/FinalPdd";
import LoginFields from "@/pages/LoginFields";
import FtpConfig from "@/pages/FtpConfig";
import { Loader2 } from "lucide-react";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="animate-spin text-emerald-500" size={32} /></div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <Toaster position="top-right" richColors />
      <AuthProvider>
        <InstallPrompt />
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/login" element={<Login />} />
            <Route path="/partner-signup" element={<PartnerSignup />} />
            <Route path="/partner-signup/:token" element={<PartnerSignup />} />
            <Route path="/portal" element={<ProtectedRoute><PartnerPortal /></ProtectedRoute>} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/dashboard" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
              <Route index element={<Dashboard />} />
              <Route path="leads" element={<Leads />} />
              <Route path="pipeline" element={<Pipeline />} />
              <Route path="partners" element={<Partners />} />
              <Route path="lenders" element={<Lenders />} />
              <Route path="commissions" element={<Commissions />} />
              <Route path="reports" element={<Reports />} />
              <Route path="notifications" element={<Notifications />} />
              <Route path="users" element={<Users />} />
              <Route path="products" element={<Products />} />
              <Route path="roles" element={<Roles />} />
              <Route path="partner-types" element={<PartnerTypes />} />
              <Route path="email-settings" element={<EmailSettings />} />
              <Route path="backup" element={<DataBackup />} />
              <Route path="email-notifications" element={<EmailNotifications />} />
              <Route path="marketing" element={<Marketing />} />
              <Route path="meta-settings" element={<MetaSettings />} />
              <Route path="finance" element={<Finance />} />
              <Route path="bill-items" element={<BillItems />} />
              <Route path="audit-logs" element={<Audit />} />
              <Route path="website" element={<Website />} />
              <Route path="master-documents" element={<MasterDocuments />} />
              <Route path="proposal-format" element={<ProposalFormat />} />
              <Route path="bill-format" element={<BillFormat />} />
              <Route path="role-hierarchy" element={<RoleHierarchy />} />
              <Route path="field-rules" element={<FieldRules />} />
              <Route path="agreement-format" element={<AgreementFormat />} />
              <Route path="leegality" element={<Leegality />} />
              <Route path="pdd" element={<Pdd />} />
              <Route path="ops-approval" element={<OpsApproval />} />
              <Route path="company-banks" element={<CompanyBanks />} />
              <Route path="undertaking-format" element={<UndertakingFormat />} />
              <Route path="ops-check" element={<OpsCheck />} />
              <Route path="offers" element={<OffersView />} />
              <Route path="offers-admin" element={<Offers />} />
              <Route path="final-pdd" element={<FinalPdd />} />
              <Route path="login-fields" element={<LoginFields />} />
              <Route path="ftp-config" element={<FtpConfig />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
